/**
 * @author       grady@zumper.com (Grady Kuhnline <gradykuhnline>)
 * @copyright    Copyright (c) 2019, Zumper
 * @description
 */

import { LoadableComponent } from '@loadable/component'
import { Renderable } from '@zcode/render-props'
import React from 'react'
import { ErrorBoundary, ErrorBoundaryProps } from 'react-error-boundary'

import {
  LazySectionContainer,
  LazySectionContainerProps,
} from './LazySectionContainer'

export interface LazyLoadableSectionProps<ForwardProps extends object>
  extends Omit<
    LazySectionContainerProps<ForwardProps>,
    'component' | 'fallback'
  > {
  errorFallback?: ErrorBoundaryProps['FallbackComponent']
  loadable: LoadableComponent<ForwardProps>
  loadingFallback?: Renderable<ForwardProps>
  onRenderError?: ErrorBoundaryProps['onError']
}

export const LazyLoadableSection = <ForwardProps extends object>({
  as,
  errorFallback,
  fallbackStyle,
  forceInView,
  forwardProps,
  lazySectionClassName,
  loadable,
  loadingFallback,
  onRenderError,
  root,
  rootMargin = '300px',
  threshold,
}: LazyLoadableSectionProps<ForwardProps>) => {
  return (
    // errorFallback might be undefined, but ErrorBoundary doesn't like that
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    <ErrorBoundary FallbackComponent={errorFallback!} onError={onRenderError}>
      <LazySectionContainer<ForwardProps>
        as={as}
        component={loadable}
        fallback={loadingFallback}
        fallbackStyle={fallbackStyle}
        forceInView={forceInView}
        forwardProps={forwardProps}
        lazySectionClassName={lazySectionClassName}
        root={root}
        rootMargin={rootMargin}
        threshold={threshold}
      />
    </ErrorBoundary>
  )
}
